import { get_token } from "@/router";

const platform_switch_url = process.env.VUE_APP_PLATFORM_SWITCH_URL;
const default_platform_code = "proba_aicampus";
const platform_labels = {
  proba_aicampus: "AICAMPUS VSI"
};
const requested_platform_code = (
  process.env.VUE_APP_REQUESTED_PLATFORM_CODES || default_platform_code
).trim();

function format_platform_label(code) {
  if (platform_labels[String(code)]) {
    return platform_labels[String(code)];
  }

  return code
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const configured_platform = requested_platform_code
  ? {
      id: requested_platform_code,
      label: format_platform_label(requested_platform_code),
      requested_platform_code,
      description: `Open ${format_platform_label(requested_platform_code)}`
    }
  : null;

const platforms = configured_platform ? [configured_platform] : [
  {
    id: default_platform_code,
    label: platform_labels[String(default_platform_code)],
    requested_platform_code: default_platform_code,
    description: `Open ${platform_labels[String(default_platform_code)]}`
  }
];

async function parse_response_body(response) {
  const content_type = response.headers.get("content-type") || "";

  if (content_type.includes("application/json")) {
    return response.json().catch(() => ({}));
  }

  const message = await response.text().catch(() => "");
  return message ? { message } : {};
}

function require_value(value, message) {
  if (!value) {
    throw new Error(message);
  }

  return value;
}

function get_response_error(data) {
  return data.error || data.message || "Could not switch platform.";
}

function get_redirect_url(data) {
  return data.redirect_url || data.url;
}

function get_safe_request_url(raw_url) {
  const parsed_url = new URL(String(raw_url || ""), window.location.origin);
  const allowed_protocols = ["http:", "https:"];

  if (!allowed_protocols.includes(parsed_url.protocol)) {
    throw new Error("Platform switch URL is not configured.");
  }

  return parsed_url.href;
}

function get_safe_redirect_url(redirect_url) {
  const parsed_url = new URL(String(redirect_url || ""), window.location.origin);
  const allowed_protocols = ["http:", "https:"];

  if (!allowed_protocols.includes(parsed_url.protocol)) {
    throw new Error("Platform response included an unsupported redirect URL.");
  }

  return parsed_url.href;
}

async function send_platform_request(request) {
  const http_client = window.fetch.bind(window);
  return http_client(request);
}

async function request_platform_redirect(platform) {
  const auth_token = require_value(
    get_token(),
    "Your session could not be found. Please sign in again."
  );
  const switch_url = require_value(
    platform_switch_url,
    "Platform switch URL is not configured."
  );
  const request = new Request(get_safe_request_url(switch_url), {
    method: "POST",
    headers: {
      Authorization: `Token ${auth_token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      requested_platform_code: platform.requested_platform_code || platform.id
    })
  });
  const response = await send_platform_request(request);
  const data = await parse_response_body(response);

  if (!response.ok) {
    throw new Error(get_response_error(data));
  }

  return require_value(
    get_redirect_url(data),
    "Platform response did not include a redirect URL."
  );
}

export default {
  name: "PlatformSwitcher",

  data() {
    return ({
      error_message: "",
      has_clicked_switch: false,
      is_loading: null,
      platforms
    });
  },

  computed: {
    primary_platform() {
      return this.platforms.find((platform) => !platform.disabled) || this.platforms[0] || null;
    },

    is_switching() {
      return Boolean(this.is_loading);
    }
  },

  methods: {
    switch_to_primary_platform() {
      return this.redirect_to_platform(this.primary_platform);
    },

    start_platform_redirect(platform) {
      this.error_message = "";
      this.has_clicked_switch = true;
      this.is_loading = platform.id;
    },

    finish_platform_redirect(error, is_redirecting) {
      if (error) {
        this.error_message = error.message || "Could not switch platform.";
      }

      if (!is_redirecting) {
        this.is_loading = null;
      }
    },

    async redirect_to_platform(platform) {
      if (!platform || platform.disabled) return;

      let is_redirecting = false;
      this.start_platform_redirect(platform);

      try {
        const redirect_url = await request_platform_redirect(platform);
        is_redirecting = true;
        window.location.assign(get_safe_redirect_url(redirect_url));
      } catch (error) {
        this.finish_platform_redirect(error, is_redirecting);
      } finally {
        this.finish_platform_redirect(null, is_redirecting);
      }
    }
  }
};
